// Painting with a terrain chip on a stroke that crosses cut cells paints the half under the pointer
// (docs/architecture/maps.md, "Library and terrain"). A cell the stroke crossed is whole to the brush unless it is cut.
import { cornerCode, cutAt, halfAt, layCell, STRAIGHT, setBorders, type Terrain, terrainOf } from "./cuts";
import { type Edges, edgeAt, setEdges } from "./edges";
import { EMPTY, paintCells } from "./terrain";

// A cell a stroke crossed, with where its points fell in it, as fractions 0..1 from the cell's north-west corner.
export type Touch = { col: number; row: number; points: { x: number; y: number }[] };

// The terrain and edges after painting `code` ("." for Apagar) over the touched cells. On a cut cell a point in the main
// half paints its floor and one in the split-off half paints its corner; a stroke that touched only the split-off half
// stands the diagonal as a wall between the two floors when that half was rock and the diagonal has no mark, ready for a door. A stroke that
// crossed both halves paints both and writes no wall, so painting over a whole square leaves it whole. Every cell touched
// takes `border`, whichever half; Apagar writes none.
export function paintTouches(terrain: Terrain, edges: Edges, touches: Touch[], code: string, border = STRAIGHT): { terrain: Terrain; edges: Edges } {
	const corners = [...(terrain.corners ?? [])];
	const mains: Touch[] = [];
	const walls: { key: "d"; col: number; row: number }[] = [];
	for (const touch of touches) {
		const { col, row } = touch;
		const letter = cutAt(terrain.cuts, col, row);
		const halves = new Set(touch.points.map(({ x, y }) => (letter ? halfAt(letter, x, y) : "main")));
		if (!letter || !halves.size || halves.has("main")) mains.push(touch);
		if (!letter || !halves.has("corner")) continue;
		const wasRock = cornerCode(terrain.cuts, terrain.corners, col, row) === null;
		layCell(corners, col, row, code);
		const mark = { key: "d", col, row } as const;
		if (wasRock && code !== EMPTY && !halves.has("main") && edgeAt(edges, mark) === EMPTY) walls.push(mark);
	}
	return {
		terrain: terrainOf(
			paintCells(terrain.rows, mains, code),
			terrain.cuts,
			corners,
			code === EMPTY ? terrain.borders : setBorders(terrain.borders, touches, border),
		),
		edges: setEdges(edges, walls, "m"),
	};
}
